/**
 * WalkCompanion Authentication Service
 * Cross-platform authentication service supporting multiple providers
 */

import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { FirebaseApp } from 'firebase/app';
import { ref, get, set, getDatabase } from 'firebase/database';

import {
  User,
  UserProfile,
  Organization,
  AuthProvider,
  UserRole
} from '../types';
import { databasePaths } from '../config/environmentConfig';

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  selectedOrganization: Organization | null;
}

export interface AuthListener {
  (state: AuthState): void;
}

export interface GoogleSignInResult {
  idToken: string;
  accessToken: string;
  user: {
    email: string;
    displayName: string;
    photoURL?: string;
  };
}

export interface AppleSignInResult {
  idToken: string;
  nonce: string;
  user: {
    email: string;
    displayName: string;
  };
}

export interface AzureADSignInResult {
  idToken: string;
  accessToken: string;
  user: {
    email: string;
    displayName: string;
    organizationId: string;
  };
}

export interface EmailSignInResult {
  email: string;
  password: string;
}

export class AuthService {
  private auth: Auth;
  private app: FirebaseApp;
  private database: any;
  private authStateListeners: AuthListener[] = [];
  private currentState: AuthState = {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    selectedOrganization: null,
  };
  private unsubscribeAuthState: (() => void) | null = null;
  private updateStateTimeout: ReturnType<typeof setTimeout> | null = null;
  private isDebugMode: boolean;
  private environment: 'dev' | 'prod';

  constructor(app: FirebaseApp, environment: 'dev' | 'prod' = 'dev') {
    this.environment = environment;
    console.log('[AuthService] Initializing with app:', app.name);

    // Check if we're in debug mode (web app only)
    this.isDebugMode = typeof window !== 'undefined' && (process.env.REACT_APP_DEBUG === 'true' || process.env.REACT_APP_DEBUG === '1');

    if (this.isDebugMode) {
      console.log('[AuthService] App options:', app.options);
    } else {
      console.log('[AuthService] App options: [config hidden - set REACT_APP_DEBUG=true to show]');
    }

    this.auth = getAuth(app);
    this.app = app; // Store the app instance
    this.database = getDatabase(app);
    console.log('[AuthService] Firebase Auth and Database initialized');

    this.initializeAuthStateListener();
  }

  /**
   * Initialize Firebase Auth state listener
   */
  private initializeAuthStateListener(): void {
    console.log('[AuthService] Initializing Firebase Auth state listener...');

    this.unsubscribeAuthState = onAuthStateChanged(
      this.auth,
      async (firebaseUser: FirebaseUser | null) => {
        console.log('[AuthService] Firebase Auth state changed:', {
          firebaseUser: firebaseUser ? firebaseUser.uid : 'null',
          timestamp: new Date().toISOString(),
          stack: new Error().stack?.split('\n').slice(1, 4).join('\n')
        });

        if (firebaseUser) {
          if (this.isDebugMode) {
            console.log('[AuthService] ✅ Firebase user authenticated:', firebaseUser.uid);
          } else {
            console.log('[AuthService] ✅ Firebase user authenticated: [user ID hidden - set REACT_APP_DEBUG=true to show]');
          }

          // Check if logout was recently completed - if so, sign out the user
          const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
          if (logoutCompleted === 'true') {
            console.log('[AuthService] Logout recently completed, signing out Firebase user');
            sessionStorage.removeItem('auth.logout.completed');
            await signOut(this.auth);
            this.updateState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              selectedOrganization: null,
            });
            return;
          }

          // Clear provider switch flag when user is authenticated
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem('auth.provider.switch.inprogress');
            console.log('[AuthService] Cleared provider switch flag');
          }

          const user = await this.convertFirebaseUserToUser(firebaseUser);
          this.updateState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } else {
          console.log('[AuthService] No Firebase user, checking for Azure AD session...');

          // Check if a logout was recently completed
          const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
          if (logoutCompleted === 'true') {
            console.log('[AuthService] Logout recently completed, setting to unauthenticated');
            sessionStorage.removeItem('auth.logout.completed');
            this.updateState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              selectedOrganization: null,
            });
            return;
          }

          // Only check for Azure AD session if we're not in the middle of a Google sign-in
          // and if we don't have a current authenticated user
          const isGoogleSignInInProgress = sessionStorage.getItem('auth.google.signin.inprogress');
          const hasCurrentUser = this.currentState.user && this.currentState.isAuthenticated;

          if (!isGoogleSignInInProgress && !hasCurrentUser) {
            await this.restoreAzureADSession();
          } else {
            if (isGoogleSignInInProgress) {
              console.log('[AuthService] Google sign-in in progress, skipping Azure AD session check');
            }
            if (hasCurrentUser) {
              console.log('[AuthService] Current user exists, skipping Azure AD session check');
            }
          }
        }
      },
      (error) => {
        console.error('[AuthService] Auth state change error:', error);

        // Check if this is an authentication error that should trigger a redirect
        if (this.isAuthenticationError(error)) {
          console.log('[AuthService] Authentication error detected, clearing state');
          // Clear all authentication data
          this.clearAuthenticationData();
        }

        this.updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: error.message,
        });
      }
    );
  }

  /**
   * Check if an error is an authentication error
   */
  private isAuthenticationError(error: any): boolean {
    if (!error) return false;

    // Firebase Auth errors
    if (error.code) {
      const authErrorCodes = [
        'auth/user-not-found',
        'auth/wrong-password',
        'auth/invalid-credential',
        'auth/user-disabled',
        'auth/too-many-requests',
        'auth/operation-not-allowed',
        'auth/invalid-email',
        'auth/weak-password',
        'auth/email-already-in-use',
        'auth/requires-recent-login',
        'auth/credential-already-in-use',
        'auth/user-token-expired'
      ];

      // Exclude network-related errors that shouldn't trigger login redirect
      const networkErrorCodes = [
        'auth/network-request-failed',
        'auth/too-many-requests'
      ];

      if (networkErrorCodes.includes(error.code)) {
        return false;
      }

      return authErrorCodes.includes(error.code);
    }

    // HTTP status codes
    if (error.status) {
      return error.status === 401 || error.status === 403;
    }

    // Error messages
    if (error.message) {
      const authErrorMessages = [
        'unauthenticated',
        'unauthorized',
        'authentication failed',
        'invalid token',
        'token expired',
        'session expired',
        'user must be authenticated'
      ];
      return authErrorMessages.some(msg =>
        error.message.toLowerCase().includes(msg.toLowerCase())
      );
    }

    return false;
  }

  /**
   * Clear all authentication-related data
   */
  private clearAuthenticationData(): void {
    try {
      // Clear sessionStorage
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }

      // Clear localStorage Firebase auth data
      if (typeof localStorage !== 'undefined') {
        const firebaseKeys = Object.keys(localStorage).filter(key =>
          key.startsWith('firebase:authUser:') ||
          key.startsWith('firebase:') ||
          key.includes('firebase')
        );
        firebaseKeys.forEach(key => localStorage.removeItem(key));
      }

      // Clear Azure AD tokens
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('azuread.tokens');
        sessionStorage.removeItem('auth.azure.callback.inprogress');
        sessionStorage.removeItem('auth.provider.switch.inprogress');
        sessionStorage.removeItem('auth.google.signin.inprogress');
        sessionStorage.removeItem('auth.user.role');
      }

      console.log('[AuthService] Authentication data cleared');
    } catch (error) {
      console.error('[AuthService] Error clearing authentication data:', error);
    }
  }

  /**
   * Restore Azure AD session if available
   */
  private async restoreAzureADSession(): Promise<void> {
    console.log('[AuthService] restoreAzureADSession called');
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
        console.log('[AuthService] Not in browser environment, skipping');
        return;
      }

      // Check if Azure AD callback processing is in progress
      const azureCallbackInProgress = sessionStorage.getItem('auth.azure.callback.inprogress');
      if (azureCallbackInProgress === 'true') {
        console.log('[AuthService] Azure AD callback processing in progress, skipping session restoration');
        return;
      }

      // Check if we're in the middle of a provider switch
      const providerSwitchInProgress = sessionStorage.getItem('auth.provider.switch.inprogress');
      if (providerSwitchInProgress === 'true') {
        console.log('[AuthService] Provider switch in progress, skipping session restoration');
        return;
      }

      // Check if a logout was recently completed
      const logoutCompleted = sessionStorage.getItem('auth.logout.completed');
      if (logoutCompleted === 'true') {
        console.log('[AuthService] Logout recently completed, skipping session restoration');
        sessionStorage.removeItem('auth.logout.completed');
        this.updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          selectedOrganization: null,
        });
        return;
      }

      // Check for Azure AD tokens in sessionStorage
      const azureTokens = sessionStorage.getItem('azuread.tokens');
      if (!azureTokens) {
        console.log('[AuthService] No Azure AD tokens found, setting to unauthenticated');
        this.updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          selectedOrganization: null,
        });
        return;
      }

      // Parse and validate tokens
      const tokenData = JSON.parse(azureTokens);
      const now = Date.now();
      const tokenAge = now - tokenData.timestamp;
      const tokenExpiry = tokenData.expires_in * 1000;

      if (tokenAge >= tokenExpiry) {
        console.log('[AuthService] Azure AD tokens expired, clearing session');
        sessionStorage.removeItem('azuread.tokens');
        this.updateState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          selectedOrganization: null,
        });
        return;
      }

      console.log('[AuthService] Azure AD tokens are valid, restoring session...');

      // Set provider switch flag to prevent conflicts
      sessionStorage.setItem('auth.provider.switch.inprogress', 'true');

      // Fetch user info from Microsoft Graph
      const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: {
          'Authorization': `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!graphResponse.ok) {
        throw new Error('Failed to fetch user info from Microsoft Graph');
      }

      const userInfo = await graphResponse.json();
      console.log('[AuthService] Got user info from Graph:', userInfo);

      // Don't set user ID here - let Firebase auth state listener handle it
      console.log('[AuthService] User info from Graph:', {
        id: userInfo.id,
        mail: userInfo.mail,
        userPrincipalName: userInfo.userPrincipalName,
        displayName: userInfo.displayName
      });

      const user: User = {
        id: '', // Will be set by Firebase auth state listener
        email: userInfo.mail || userInfo.userPrincipalName || '',
        displayName: userInfo.displayName || userInfo.mail?.split('@')[0] || 'Azure User',
        profileImageURL: undefined,
        // Don't assign organizationId for new users - they need to choose during onboarding
        organizationId: undefined,
        teamId: undefined,
        role: this.currentState.user?.role || 'member',
        joinedDate: new Date().toISOString(),
        totalPoints: 0,
        streak: 0,
        authProvider: 'azure',
        organizationName: undefined,
        teamName: undefined,
        profile: {
          displayName: userInfo.displayName || userInfo.mail?.split('@')[0] || 'Azure User',
          email: userInfo.mail || userInfo.userPrincipalName || '',
          organizationId: undefined,
          organizationName: undefined,
          role: this.currentState.user?.role || 'member',
          joinedDate: new Date().toISOString(),
          streak: 0,
          totalPoints: 0,
        },
      };

      // Create default organization (only if user already has an organizationId)
      const organization: Organization = {
        id: user.organizationId || 'temp-org',
        name: user.organizationName || 'Temporary Organization',
        description: user.organizationName || 'Temporary organization',
        domain: user.email.split('@')[1] || 'temp-org',
        logo: '',
        createdAt: new Date().toISOString(),
        settings: {
          allowPublicJoin: true,
          customChallenges: true,
          leaderboardVisible: true,
          requireApproval: false,
          teamCreationOpen: true
        }
      };

      // Generate the same deterministic password that was used in mapAzureADUser
      const generateDeterministicPassword = (email: string): string => {
        const secret = "AzureAD_Fitzaa_2024";
        const combined = email + secret;
        let hash = 0;
        for (let i = 0; i < combined.length; i++) {
          const char = combined.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash;
        }
        const basePassword = Math.abs(hash).toString(36).slice(0, 8);
        return `AzureAD_${basePassword}_2024!`;
      };

      const password = generateDeterministicPassword(user.email);
      console.log('[AuthService] 🔐 Signing in with Firebase Auth using email/password');

      try {
        // Sign in with Firebase Auth to restore the session
        await signInWithEmailAndPassword(this.auth, user.email, password);
        console.log('[AuthService] ✅ Firebase Auth sign-in successful for session restoration');

        // The Firebase auth state listener will handle the rest
        // Don't update state here - let the auth state listener do it
      } catch (signInError: any) {
        console.error('[AuthService] ❌ Failed to sign in with Firebase during session restoration:', signInError);

        // Clear the provider switch flag on error
        sessionStorage.removeItem('auth.provider.switch.inprogress');

        // If it's an invalid credential error, try to update the password
        if (signInError.code === 'auth/invalid-credential') {
          try {
            console.log('[AuthService] 🔄 Invalid credential, attempting to update password...');
            // Import the cloud function service
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            const functions = getFunctions(this.app);
            const updateUserPasswordFn = httpsCallable(functions, 'updateUserPassword');
            await updateUserPasswordFn({ email: user.email });
            console.log('[AuthService] ✅ Password updated, trying sign-in again...');
            // Try sign-in again with the updated password
            await signInWithEmailAndPassword(this.auth, user.email, password);
            console.log('[AuthService] ✅ Firebase Auth sign-in successful after password update');
          } catch (updateError: any) {
            console.error('[AuthService] ❌ Failed to update password:', updateError);
            // Clear the session and set to unauthenticated
            sessionStorage.removeItem('azuread.tokens');
            this.updateState({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              selectedOrganization: null,
            });
          }
        } else {
          // Clear the session and set to unauthenticated
          sessionStorage.removeItem('azuread.tokens');
          this.updateState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            selectedOrganization: null,
          });
        }
      }

    } catch (error) {
      console.error('[AuthService] Error restoring Azure AD session:', error);
      // Clear the provider switch flag on error
      sessionStorage.removeItem('auth.provider.switch.inprogress');
      // If restoration fails, set to unauthenticated
      this.updateState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        selectedOrganization: null,
      });
    }
  }

  /**
   * Get Azure AD access token from sessionStorage
   */
  private getAzureADAccessToken(): string | null {
    try {
      console.log('[AuthService] getAzureADAccessToken called');

      // Check for Azure AD tokens in sessionStorage
      const tokenData = sessionStorage.getItem('azuread.tokens');
      if (!tokenData) {
        console.log('[AuthService] No Azure AD tokens found');
        return null;
      }

      const tokens = JSON.parse(tokenData);
      console.log('[AuthService] Token data:', tokens);

      // Check if token is still valid (not expired)
      if (tokens.expires_in && tokens.timestamp) {
        const expiresOnMs = tokens.timestamp + (tokens.expires_in * 1000);
        const currentTimeMs = Date.now();

        console.log('[AuthService] Token expires on:', new Date(expiresOnMs));
        console.log('[AuthService] Current time:', new Date(currentTimeMs));
        console.log('[AuthService] Token valid for:', Math.floor((expiresOnMs - currentTimeMs) / 1000 / 60), 'more minutes');

        if (expiresOnMs > currentTimeMs) {
          console.log('[AuthService] Token is valid, returning access token');
          return tokens.access_token;
        } else {
          console.log('[AuthService] Token is expired');
          return null;
        }
      } else {
        console.log('[AuthService] No expiration info found in token data');
        return null;
      }

    } catch (error) {
      console.error('[AuthService] Error getting Azure AD access token:', error);
      return null;
    }
  }

  /**
   * Convert Firebase User to our User type
   */
  private async convertFirebaseUserToUser(firebaseUser: FirebaseUser): Promise<User> {
    console.log('[AuthService] Converting Firebase user to User object:', firebaseUser.uid);

    // Get user profile from database if available
    let userProfile = await this.getUserProfileFromDatabase(firebaseUser.uid);

    if (this.isDebugMode) {
      console.log('[AuthService] Retrieved user profile from database:', userProfile);
    } else {
      console.log('[AuthService] Retrieved user profile from database: [profile hidden - set REACT_APP_DEBUG=true to show]');
    }

    // If no profile exists, create a default one
    if (!userProfile) {
      console.log('[AuthService] No user profile found, creating default profile');
      
      const authProvider = this.determineAuthProvider(firebaseUser);

      // Try to get role from sessionStorage as fallback
      let defaultRole: UserRole = 'member';
      try {
        const persistedRole = sessionStorage.getItem('auth.user.role') as UserRole;
        if (persistedRole && ['member', 'team_lead', 'team_admin', 'org_admin', 'super_admin'].includes(persistedRole)) {
          defaultRole = persistedRole;
          console.log('[AuthService] Using persisted role for new user:', defaultRole);
        }
      } catch (error) {
        console.error('[AuthService] Error reading persisted role:', error);
      }

      userProfile = {
        displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        email: firebaseUser.email || '',
        // Don't assign organizationId for new users - they need to choose during onboarding
        organizationId: undefined,
        organizationName: undefined,
        role: defaultRole,
        joinedDate: new Date().toISOString(),
        streak: 0,
        totalPoints: 0,
      };

      // Save the default profile to database
      await this.saveUserProfileToDatabase(firebaseUser.uid, userProfile);
      console.log('[AuthService] Default user profile created and saved to database');
    }

    // Determine organization name based on auth provider
    const authProvider = this.determineAuthProvider(firebaseUser);
    let organizationName = userProfile?.organizationName;

    // Set default organization name based on auth provider if not set in profile
    if (!organizationName) {
      if (authProvider === 'email') {
        organizationName = 'University Hospitals';
      } else if (authProvider === 'google') {
        organizationName = 'Individual User';
      }
    }

    // Check for family member status and update organization info if needed
    let familyStatus: string | undefined;
    let familyMemberOf: string | undefined;
    try {
      console.log('[AuthService] Checking family member status for user ID:', firebaseUser.uid);

      // Use proper environment to get family status
      const familyStatusPath = `${databasePaths.user_family_status(this.environment)}/${firebaseUser.uid}`;

      const familyStatusRef = ref(this.database, familyStatusPath);
      const snapshot = await get(familyStatusRef);
      const familyStatusData = snapshot.val();
      console.log('[AuthService] Family status data:', familyStatusData);

      if (familyStatusData?.isFamilyMember) {
        familyStatus = 'family_member';
        familyMemberOf = familyStatusData.familyMemberOf;
        console.log('[AuthService] User is a family member of:', familyMemberOf);

        // Get family member info to get organization details
        const familyMemberInfo = await this.getFamilyMemberInfo(firebaseUser.uid, familyStatusData.familyMemberOf);
        console.log('[AuthService] Family member info:', familyMemberInfo);

        if (familyMemberInfo) {
          // Override organization info with family member's organization
          userProfile.organizationId = familyMemberInfo.organizationId;
          userProfile.organizationName = familyMemberInfo.organizationName;
          organizationName = familyMemberInfo.organizationName;
          console.log('[AuthService] Updated organization info for family member:', {
            organizationId: familyMemberInfo.organizationId,
            organizationName: familyMemberInfo.organizationName
          });
        }
      } else {
        console.log('[AuthService] User is not a family member or no family status data found');
      }
    } catch (error) {
      console.log('[AuthService] No family member status found or error checking:', error);
    }

    // Determine role with better fallback logic
    let role: UserRole = 'member';
    if (userProfile?.role) {
      role = userProfile.role;
      console.log('[AuthService] Using role from database profile:', role);
    } else if (this.currentState.user?.role) {
      role = this.currentState.user.role;
      console.log('[AuthService] Using role from current state:', role);
    } else {
      // Try to get role from sessionStorage as fallback
      try {
        const persistedRole = sessionStorage.getItem('auth.user.role') as UserRole;
        if (persistedRole && ['member', 'team_lead', 'team_admin', 'org_admin', 'super_admin'].includes(persistedRole)) {
          role = persistedRole;
          console.log('[AuthService] Using role from sessionStorage:', role);
        } else {
          console.log('[AuthService] No valid role found in sessionStorage, defaulting to member');
        }
      } catch (error) {
        console.error('[AuthService] Error reading role from sessionStorage:', error);
        console.log('[AuthService] Defaulting to member role');
      }
    }

    const user = {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || userProfile?.displayName || firebaseUser.email?.split('@')[0] || 'User',
      profileImageURL: firebaseUser.photoURL || undefined,
      organizationId: userProfile?.organizationId,
      teamId: userProfile?.teamId,
      role: role,
      joinedDate: userProfile?.joinedDate || new Date().toISOString(),
      totalPoints: userProfile?.totalPoints || 0,
      streak: userProfile?.streak || 0,
      authProvider: authProvider,
      organizationName: organizationName,
      teamName: userProfile?.teamName,
      profile: userProfile || undefined,
      familyStatus: familyStatus,
      familyMemberOf: familyMemberOf,
    };

    if (this.isDebugMode) {
      console.log('[AuthService] Final user object created:', {
        id: user.id,
        email: user.email,
        displayName: user.displayName,
        role: user.role,
        organizationName: user.organizationName
      });
    } else {
      console.log('[AuthService] Final user object created: [user details hidden - set REACT_APP_DEBUG=true to show]');
    }

    return user;
  }

  /**
   * Save user profile to database
   */
  public async saveUserProfileToDatabase(userId: string, userProfile: UserProfile): Promise<void> {
    try {
      console.log('[AuthService] Saving user profile to database for userId:', userId, userProfile);

      // Check if Firebase app is properly initialized
      if (!this.app) {
        console.error('[AuthService] Firebase app is not initialized');
        return;
      }

      // Try to use cloud function if available (web app)
      try {
        console.log('[AuthService] Attempting to use cloud function for user profile creation...');
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions(this.app);
        const createUserProfileFn = httpsCallable(functions, 'createUserProfile');

        const userData = {
          displayName: userProfile?.displayName || '',
          email: userProfile?.email || '',
          organizationId: userProfile?.organizationId,
          organizationName: userProfile?.organizationName,
          role: userProfile?.role || 'member',
          teamId: userProfile?.teamId,
          teamName: userProfile?.teamName,
          authProvider: 'email', // UserProfile doesn't have authProvider, default to email
        };

        console.log('[AuthService] Calling cloud function with userData:', userData);
        const result = await createUserProfileFn({ userData });
        console.log('[AuthService] Cloud function result:', result);
        console.log('[AuthService] User profile saved via cloud function successfully');
        return;
      } catch (cloudFunctionError) {
        console.warn('[AuthService] Cloud function failed, falling back to direct database write:', cloudFunctionError);
      }

      // Fallback to direct database write (mobile app)
      const { getDatabase, ref, set } = await import('firebase/database');
      const userRef = ref(getDatabase(this.app), `${databasePaths.users(this.environment)}/${userId}`);
      await set(userRef, userProfile);

      console.log('[AuthService] User profile saved to database successfully');
    } catch (error) {
      console.error('[AuthService] Error saving user profile to database:', error);
    }
  }

  /**
   * Get user profile from database
   */
  private async getUserProfileFromDatabase(userId: string): Promise<UserProfile | null> {
    try {
      console.log('[AuthService] Fetching user profile for userId:', userId);

      // Check if Firebase app is properly initialized
      if (!this.app) {
        console.error('[AuthService] Firebase app is not initialized');
        return null;
      }

      // Fetch user profile from Firebase Realtime Database
      const { getDatabase, ref, get } = await import('firebase/database');
      const userRef = ref(getDatabase(this.app), `${databasePaths.users(this.environment)}/${userId}`);
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        const userData = snapshot.val();

        if (this.isDebugMode) {
          console.log('[AuthService] User data from database:', userData);
        } else {
          console.log('[AuthService] User data from database: [data hidden - set REACT_APP_DEBUG=true to show]');
        }

        // Handle both database structures: role at root level or nested under profile
        const role = userData.role || userData.profile?.role || 'member';
        const displayName = userData.displayName || userData.profile?.displayName || '';
        const email = userData.email || userData.profile?.email || '';
        const joinedDate = userData.joinedDate || userData.profile?.joinedDate || new Date().toISOString();
        const organizationId = userData.organizationId || userData.profile?.organizationId;
        const organizationName = userData.organizationName || userData.profile?.organizationName;
        const streak = userData.streak || userData.profile?.streak || 0;
        const totalPoints = userData.totalPoints || userData.profile?.totalPoints || 0;
        const teamId = userData.teamId || userData.profile?.teamId;
        const teamName = userData.teamName || userData.profile?.teamName;

        const profile = {
          displayName,
          email,
          joinedDate,
          organizationId,
          organizationName,
          role,
          streak,
          totalPoints,
          teamId,
          teamName,
        };

        if (this.isDebugMode) {
          console.log('[AuthService] Parsed user profile:', profile);
        } else {
          console.log('[AuthService] Parsed user profile: [profile hidden - set REACT_APP_DEBUG=true to show]');
        }
        return profile;
      } else {
        console.log('[AuthService] No user data found in database for userId:', userId);
        return null;
      }
    } catch (error) {
      console.error('[AuthService] Error fetching user profile:', error);
      return null;
    }
  }

  /**
   * Determine auth provider from Firebase user
   */
  private determineAuthProvider(firebaseUser: FirebaseUser): AuthProvider {
    if (firebaseUser.providerData.length > 0) {
      const provider = firebaseUser.providerData[0]?.providerId;
      switch (provider) {
        case 'google.com':
          return 'google';
        case 'apple.com':
          return 'google'; // Apple maps to google for compatibility
        case 'password':
          return 'email';
        default:
          return 'email';
      }
    }
    return 'email';
  }

  /**
   * Update auth state and notify listeners
   */
  private updateState(newState: Partial<AuthState>): void {
    if (this.isDebugMode) {
      console.log('[AuthService] updateState called with:', {
        newState,
        currentUser: this.currentState.user?.id,
        currentIsAuthenticated: this.currentState.isAuthenticated,
        currentIsLoading: this.currentState.isLoading
      });
    } else {
      console.log('[AuthService] updateState called with: [details hidden - set REACT_APP_DEBUG=true to show]');
    }

    // Clear any existing timeout
    if (this.updateStateTimeout) {
      clearTimeout(this.updateStateTimeout);
    }

    // Debounce state updates to prevent rapid changes
    this.updateStateTimeout = setTimeout(() => {
      this.currentState = { ...this.currentState, ...newState };

      if (this.isDebugMode) {
        console.log('[AuthService] State updated to:', {
          user: this.currentState.user?.id,
          isAuthenticated: this.currentState.isAuthenticated,
          isLoading: this.currentState.isLoading
        });
      } else {
        console.log('[AuthService] State updated to: [details hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Persist user role to sessionStorage for page refresh recovery
      if (newState.user?.role) {
        try {
          sessionStorage.setItem('auth.user.role', newState.user.role);
          if (this.isDebugMode) {
            console.log('[AuthService] Persisted user role to sessionStorage:', newState.user.role);
          } else {
            console.log('[AuthService] Persisted user role to sessionStorage: [role hidden - set REACT_APP_DEBUG=true to show]');
          }
        } catch (error) {
          console.error('[AuthService] Error persisting user role:', error);
        }
      }

      this.notifyListeners();
    }, 100); // 100ms debounce
  }

  /**
   * Notify all auth state listeners
   */
  private notifyListeners(): void {
    this.authStateListeners.forEach(listener => listener(this.currentState));
  }

  /**
   * Subscribe to auth state changes
   */
  public subscribe(listener: AuthListener): () => void {
    this.authStateListeners.push(listener);
    // Immediately call with current state
    listener(this.currentState);

    // Return unsubscribe function
    return () => {
      const index = this.authStateListeners.indexOf(listener);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  /**
   * Get current auth state
   */
  public getCurrentState(): AuthState {
    return this.currentState;
  }

  /**
   * Sign in with Google
   */
  public async signInWithGoogle(result: GoogleSignInResult): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });

      // Set flag to indicate Google sign-in is in progress
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('auth.google.signin.inprogress', 'true');
      }

      const credential = GoogleAuthProvider.credential(
        result.idToken,
        result.accessToken
      );

      await signInWithCredential(this.auth, credential);
      console.log('Successfully signed in with Google');

      // Clear the flag after successful sign-in
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('auth.google.signin.inprogress');
      }

    } catch (error) {
      console.error('Google sign-in error:', error);

      // Clear the flag on error
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('auth.google.signin.inprogress');
      }

      this.updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Google sign-in failed',
      });
      throw error;
    }
  }

  /**
   * Sign in with Apple
   */
  public async signInWithApple(result: AppleSignInResult): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });

      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: result.idToken,
        rawNonce: result.nonce
      });

      await signInWithCredential(this.auth, credential);
      console.log('Successfully signed in with Apple');

    } catch (error) {
      console.error('Apple sign-in error:', error);
      this.updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Apple sign-in failed',
      });
      throw error;
    }
  }

  /**
   * Sign in with Azure AD
   */
  public async signInWithAzureAD(mappedUser: any, organization: Organization): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });
      if (this.isDebugMode) {
        console.log('[AuthService] signInWithAzureAD called with mappedUser:', mappedUser, organization);
      } else {
        console.log('[AuthService] signInWithAzureAD called with: [user and organization details hidden - set REACT_APP_DEBUG=true to show]');
      }

      // Create user profile
      const userProfile = {
        displayName: mappedUser.displayName,
        email: mappedUser.email,
        organizationId: mappedUser.organizationId,
        organizationName: mappedUser.organizationName,
        role: mappedUser.role,
        joinedDate: new Date().toISOString(),
        streak: 0,
        totalPoints: 0,
      };

      // Save user profile to database
      await this.saveUserProfileToDatabase(mappedUser.uid, userProfile);

      // Set the selected organization
      this.updateState({ selectedOrganization: organization });

      // Update state with the authenticated user from Firebase Functions
      this.updateState({
        user: {
          id: mappedUser.uid,
          email: mappedUser.email,
          displayName: mappedUser.displayName,
          organizationId: mappedUser.organizationId,
          organizationName: mappedUser.organizationName,
          profile: userProfile,
          role: mappedUser.role,
          joinedDate: new Date().toISOString(),
          totalPoints: 0,
          streak: 0,
          authProvider: 'azure',
        } as any,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Clear the Azure AD callback processing flag
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('auth.azure.callback.inprogress');
        console.log('[AuthService] Cleared Azure AD callback processing flag');
      }

      console.log('[AuthService] \u2705 Azure AD sign-in completed successfully');
    } catch (error) {
      console.error('[AuthService] Azure AD sign-in error:', error);
      let errorMessage = 'Azure AD sign-in failed';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      this.updateState({
        isLoading: false,
        error: errorMessage,
      });
      throw error;
    }
  }

  /**
   * Sign in with email and password
   */
  public async signInWithEmail(email: string, password: string): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });

      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      console.log('Successfully signed in with email:', userCredential.user.uid);

    } catch (error) {
      console.error('Email sign-in error:', error);
      this.updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Email sign-in failed',
      });
      throw error;
    }
  }

  /**
   * Sign up with email and password
   * Creates Firebase user and optionally sends verification email
   */
  public async signUpWithEmail(
    email: string,
    password: string,
    displayName?: string,
    options?: { skipEmailVerification?: boolean }
  ): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });

      // Create the Firebase user account
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;

      // Update display name if provided
      if (displayName) {
        try {
          await updateProfile(user, { displayName });
        } catch (updateError) {
          console.warn('Failed to update display name:', updateError);
          // Non-critical, continue
        }
      }

      // Send verification email unless skipped (for custom email verification)
      if (!options?.skipEmailVerification) {
        const verificationUrl = typeof window !== 'undefined' 
          ? `${window.location.origin}/verify-email`
          : undefined;
        await sendEmailVerification(user, verificationUrl ? { url: verificationUrl } : undefined);
        console.log('✅ User account created and verification email sent');
      } else {
        console.log('✅ User account created (email verification skipped - using custom email)');
      }

    } catch (error: any) {
      console.error('❌ Sign-up error:', error);
      
      // Log detailed error information for debugging
      if (error?.code) {
        console.error('Error code:', error.code);
      }
      if (error?.message) {
        console.error('Error message:', error.message);
      }
      if (error?.customData) {
        console.error('Error customData:', error.customData);
      }
      if (error?.stack) {
        console.error('Error stack:', error.stack);
      }
      
      this.updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sign-up failed',
      });
      throw error;
    }
  }

  /**
   * Reset password
   * Note: This method is kept for backward compatibility but should be replaced
   * with custom password reset (sendCustomPasswordReset) in web app
   */
  public async resetPassword(email: string): Promise<void> {
    try {
      this.updateState({ isLoading: true, error: null });

      await sendPasswordResetEmail(this.auth, email);
      console.log('Password reset email sent to:', email);

      this.updateState({ isLoading: false });

    } catch (error) {
      console.error('Password reset error:', error);
      this.updateState({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Password reset failed',
      });
      throw error;
    }
  }

  /**
   * Sign out
   */
  public async signOut(): Promise<void> {
    try {
      console.log('[AuthService] Signing out...');

      // Sign out from Firebase
      await signOut(this.auth);
      console.log('[AuthService] Firebase sign out successful');

      // Clear Firebase Auth localStorage to prevent auto-restoration
      if (typeof localStorage !== 'undefined') {
        const firebaseKeys = Object.keys(localStorage).filter(key =>
          key.startsWith('firebase:authUser:') ||
          key.startsWith('firebase:') ||
          key.includes('firebase')
        );
        firebaseKeys.forEach(key => {
          localStorage.removeItem(key);
          console.log('[AuthService] Cleared Firebase localStorage key:', key);
        });
      }

      // Clear Azure AD tokens from sessionStorage
      this.clearAzureADTokens();
      console.log('[AuthService] Azure AD tokens cleared');

      // Update state to unauthenticated
      this.updateState({
        user: null,
        isAuthenticated: false,
        selectedOrganization: null,
      });

      console.log('[AuthService] Sign out completed successfully');

    } catch (error) {
      console.error('[AuthService] Sign out error:', error);
      throw error;
    }
  }

  /**
   * Clear Azure AD tokens from sessionStorage
   */
  private clearAzureADTokens(): void {
    try {
      if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;

      // Clear Azure AD tokens
      sessionStorage.removeItem('azuread.tokens');
      console.log('[AuthService] Cleared Azure AD tokens');

      // Clear the Azure AD callback processing flag
      sessionStorage.removeItem('auth.azure.callback.inprogress');
      console.log('[AuthService] Cleared Azure AD callback processing flag');

      // Clear the provider switch flag
      sessionStorage.removeItem('auth.provider.switch.inprogress');
      console.log('[AuthService] Cleared provider switch flag');

      // Clear the logout flag if it exists
      sessionStorage.removeItem('auth.logout.completed');

      // Clear persisted user role
      sessionStorage.removeItem('auth.user.role');
      if (this.isDebugMode) {
        console.log('[AuthService] Cleared persisted user role');
      } else {
        console.log('[AuthService] Cleared persisted user role: [details hidden - set REACT_APP_DEBUG=true to show]');
      }

    } catch (error) {
      console.error('[AuthService] Error clearing Azure AD tokens:', error);
    }
  }

  /**
   * Set selected organization
   */
  public setSelectedOrganization(organization: Organization | null): void {
    this.updateState({ selectedOrganization: organization });
  }

  /**
   * Get current user
   */
  public getCurrentUser(): User | null {
    return this.currentState.user;
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return this.currentState.isAuthenticated;
  }

  /**
   * Get Firebase Auth instance
   */
  public getAuth(): Auth {
    return this.auth;
  }

  /**
   * Force refresh the current user profile from database
   * This is useful when custom claims have been updated
   */
  public async forceRefreshUserProfile(): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      console.log('[AuthService] No current user to refresh');
      return;
    }

    console.log('[AuthService] 🔄 Force refreshing user profile...');
    try {
      // Convert Firebase user to our User type (this will fetch fresh profile from DB)
      const refreshedUser = await this.convertFirebaseUserToUser(currentUser);

      // Update the state with the refreshed user
      this.updateState({
        user: refreshedUser,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      console.log('[AuthService] ✅ User profile refreshed successfully');
      console.log('[AuthService] New user role:', refreshedUser.role);
    } catch (error) {
      console.error('[AuthService] ❌ Error refreshing user profile:', error);
    }
  }

  /**
   * Cleanup resources
   */
  public cleanup(): void {
    console.log('[AuthService] Cleaning up...');

    // Clear any pending state update
    if (this.updateStateTimeout) {
      clearTimeout(this.updateStateTimeout);
      this.updateStateTimeout = null;
    }

    // Remove auth state listener
    if (this.unsubscribeAuthState) {
      this.unsubscribeAuthState();
      this.unsubscribeAuthState = null;
    }

    // Clear all listeners
    this.authStateListeners = [];
  }


  /**
   * Get family member info including organization details
   */
  private async getFamilyMemberInfo(userId: string, familyMemberOf: string): Promise<any> {
    try {
      // Get the current user's email to match against family member records
      const userRef = ref(this.database, `users/${userId}`);
      const userSnapshot = await get(userRef);
      const userData = userSnapshot.val();

      if (!userData?.email) {
        return null;
      }

      console.log('[AuthService] getFamilyMemberInfo - looking for family members of:', familyMemberOf);
      console.log('[AuthService] getFamilyMemberInfo - user email:', userData.email);

      // Try different environment paths for family members
      const paths = [
        `dev/family_members/${familyMemberOf}/members`,
        `prod/family_members/${familyMemberOf}/members`,
        `family_members/${familyMemberOf}/members`
      ];

      for (const path of paths) {
        console.log('[AuthService] getFamilyMemberInfo - checking path:', path);
        const familyMembersRef = ref(this.database, path);
        const snapshot = await get(familyMembersRef);
        const familyMembers = snapshot.val() || {};

        console.log('[AuthService] getFamilyMemberInfo - found members:', Object.keys(familyMembers));

        // Find the family member record for this user by email
        for (const memberId in familyMembers) {
          const member = familyMembers[memberId];
          console.log('[AuthService] getFamilyMemberInfo - checking member:', memberId, member.email);
          if (member.email === userData.email) {
            console.log('[AuthService] getFamilyMemberInfo - found matching member:', member);
            return member;
          }
        }
      }

      console.log('[AuthService] getFamilyMemberInfo - no matching member found');
      return null;
    } catch (error) {
      console.log('[AuthService] Error getting family member info:', error);
      return null;
    }
  }
} 